import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Box,
  Grid,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Typography,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import { spacing } from "@mui/system";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ConvertProspectEntitiesDialog from "../../components/ConvertProspectEntitiesDialog";
import OnboardingCustomerWizard from "../../components/OnboardingCustomerWizard";
import ViewProspectManagementDialog from "../../components/ViewProspectManagementDialog";
import axios from "axios";
import { usePermissions } from "../../hooks/usePermissions";
import { CAPABILITIES } from "../../auth/permissions";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);

const ProspectManagement = () => {
  const { hasPermission } = usePermissions();
  const canWriteProspects = hasPermission(CAPABILITIES.prospectsWrite, "W");
  const canConvertProspects = hasPermission(CAPABILITIES.prospectsConvert, "X");
  const [prospects, setProspects] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [entities, setEntities] = useState([]);
  const [selectedEntityIds, setSelectedEntityIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [notesCount, setNotesCount] = useState(0);
  const [currentTab, setCurrentTab] = useState(0);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertProspect, setConvertProspect] = useState(null);

  const fetchProspects = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/v1/prospects");
      console.log("Prospects API response:", response);
      console.log("Prospects data:", response.data);
      
      // Handle different response structures
      let prospectsData = [];
      if (Array.isArray(response.data)) {
        prospectsData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        prospectsData = response.data.data;
      } else if (response.data && response.data.items) {
        prospectsData = response.data.items;
      }
      
      console.log("Processed prospects data:", prospectsData);
      console.log("Number of prospects:", prospectsData.length);
      setProspects(prospectsData);
    } catch (error) {
      console.error("Error fetching prospects:", error);
      console.error("Error response:", error.response);
      console.error("Error message:", error.message);
      setProspects([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProspects();
  }, []);

  const handleOpenWizard = () => {
    setWizardOpen(true);
  };

  const handleCloseWizard = () => {
    setWizardOpen(false);
  };

  const handleWizardComplete = (data) => {
    console.log("Wizard completed with data:", data);
    setWizardOpen(false);
    // Refresh prospects list after creating a new prospect
    fetchProspects();
  };

  const handleViewProspect = (prospect) => {
    setSelectedProspect(prospect);
    setViewDialogOpen(true);
  };

  const handleConvertToCustomer = (prospect) => {
    setConvertProspect(prospect);
    setConvertDialogOpen(true);
  };

  const handleDeleteProspect = async (prospect) => {
    setSelectedProspect(prospect);
    try {
      // Fetch full prospect details to get entities
      const response = await axios.get(`/api/v1/prospects/${prospect.id}`);
      console.log("Prospect details:", response.data);
      console.log("Entity IDs from prospect:", response.data.entityIds);
      
      // Fetch entities if there are entityIds
      if (response.data.entityIds && response.data.entityIds.length > 0) {
        const entityPromises = response.data.entityIds.map((entityId) =>
          axios.get(`/api/v1/entities/${entityId}`).catch((error) => {
            console.error(`Error fetching entity ${entityId}:`, error);
            return null;
          })
        );
        const entityResponses = await Promise.all(entityPromises);
        const fetchedEntities = entityResponses
          .filter((res) => res !== null)
          .map((res) => {
            const entity = res.data;
            // Ensure entityId is set correctly (could be entityId or id)
            const entityId = entity.entityId || entity.id;
            console.log("Entity data:", entity, "Resolved entityId:", entityId);
            return {
              ...entity,
              entityId: entityId,
            };
          });
        console.log("Fetched entities for delete:", fetchedEntities);
        setEntities(fetchedEntities);
        setSelectedEntityIds([]);
      } else {
        console.log("No entities found for this prospect");
        setEntities([]);
        setSelectedEntityIds([]);
      }
      
      // Fetch notes count
      try {
        const notesResponse = await axios.get(`/api/v1/prospectnotes/prospect/${prospect.id}`);
        const notes = notesResponse.data || [];
        setNotesCount(notes.length);
      } catch (error) {
        console.error("Error fetching prospect notes count:", error);
        setNotesCount(0);
      }
      
      setDeleteDialogOpen(true);
    } catch (error) {
      console.error("Error fetching prospect details:", error);
      alert("Error fetching prospect details: " + (error.response?.data?.message || error.message));
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedProspect) return;
    
    setDeleting(true);
    try {
      // Delete prospect notes first
      try {
        const notesResponse = await axios.get(`/api/v1/prospectnotes/prospect/${selectedProspect.id}`);
        const notes = notesResponse.data || [];
        console.log(`Found ${notes.length} notes to delete for prospect ${selectedProspect.id}`);
        
        for (const note of notes) {
          try {
            const noteId = note.id || note.noteId;
            if (noteId) {
              await axios.delete(`/api/v1/prospectnotes/${noteId}`);
              console.log(`Deleted note ${noteId}`);
            }
          } catch (error) {
            console.error(`Error deleting note ${note.id || note.noteId}:`, error);
          }
        }
      } catch (error) {
        console.error("Error fetching or deleting prospect notes:", error);
        // Continue with deletion even if notes deletion fails
      }
      
      // Delete selected entities
      for (const entityId of selectedEntityIds) {
        try {
          await axios.delete(`/api/v1/entities/${entityId}`);
        } catch (error) {
          console.error(`Error deleting entity ${entityId}:`, error);
        }
      }
      
      // Delete prospect
      await axios.delete(`/api/v1/prospects/${selectedProspect.id}`);
      
      // Refresh prospects list
      await fetchProspects();
      
      // Close dialog and reset state
      setDeleteDialogOpen(false);
      setSelectedProspect(null);
      setEntities([]);
      setSelectedEntityIds([]);
      setNotesCount(0);
    } catch (error) {
      console.error("Error deleting prospect:", error);
      alert("Error deleting prospect: " + (error.response?.data?.message || error.message));
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 90, hide: true },
    { field: "companyName", headerName: "Company Name", flex: 1.5 },
    { field: "contactName", headerName: "Contact Person", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    { field: "phone", headerName: "Phone", flex: 0.8 },
    {
      field: "city",
      headerName: "City",
      flex: 0.8,
      valueGetter: (params) => params.row.city || "-",
    },
    {
      field: "entityIds",
      headerName: "Entities",
      flex: 0.6,
      valueGetter: (params) => {
        const entityIds = params.row.entityIds || [];
        return entityIds.length > 0 ? entityIds.length : 0;
      },
      renderCell: (params) => {
        const entityIds = params.row.entityIds || [];
        return entityIds.length > 0 ? `${entityIds.length} entity(ies)` : "No entities";
      },
    },
    {
      field: "createDate",
      headerName: "Created Date",
      flex: 0.8,
      valueGetter: (params) => {
        if (!params.row.createDate) return "-";
        try {
          return new Date(params.row.createDate).toLocaleDateString();
        } catch {
          return params.row.createDate;
        }
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        // Only show convert button for current prospects (active === 1 or true)
        const isActive = params.row.active === 1 || params.row.active === true;
        
        return (
          <Box>
            <IconButton
              size="small"
              onClick={() => handleViewProspect(params.row)}
              aria-label="View prospect"
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
            {isActive && canConvertProspects && (
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleConvertToCustomer(params.row)}
                aria-label="Convert to customer(s)"
                title="Convert to customer(s)"
              >
                <PersonAddIcon fontSize="small" />
              </IconButton>
            )}
            {canWriteProspects && (
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteProspect(params.row)}
              aria-label="Delete prospect"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
            )}
          </Box>
        );
      },
    },
  ];

  // Filter prospects based on finalized status only
  // finalized is an integer column: 0 = not finalized, 1 = finalized
  const currentProspects = prospects.filter((prospect) => {
    const finalized = prospect.finalized; // Integer column
    // Current: not finalized (finalized !== 1)
    return finalized !== 1;
  });

  const historicProspects = prospects.filter((prospect) => {
    const finalized = prospect.finalized; // Integer column
    // Historic: finalized (finalized === 1)
    return finalized === 1;
  });

  // Get the filtered prospects based on current tab
  const filteredProspects = currentTab === 0 ? currentProspects : historicProspects;

  return (
    <React.Fragment>
      <Helmet title="Prospects" />
      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Prospects
          </Typography>
          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/customers/list">
              Customers
            </Link>
            <Typography>Prospects</Typography>
          </Breadcrumbs>
        </Grid>
        {canWriteProspects && (
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenWizard}
            sx={{ mt: 2 }}
          >
            Onboarding Customer
          </Button>
        </Grid>
        )}
      </Grid>

      <Divider my={6} />
      <Card mb={6}>
        <CardContent
          pb={1}
          sx={{
            marginBottom: 5,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Prospect Files
          </Typography>
          <Typography variant="body2" gutterBottom>
            Manage prospect files. Use the wizard to add a new prospect.
          </Typography>
        </CardContent>
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            <Tab label="Current Prospects" />
            <Tab label="Historic Prospects" />
          </Tabs>
        </Box>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Box sx={{ marginBottom: "10px" }} />
            {!loading && filteredProspects.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  {currentTab === 0
                    ? "No current prospects found. Click 'Onboarding Customer' to create a new prospect."
                    : "No historic prospects found."}
                </Typography>
              </Box>
            ) : (
              <DataGrid
                getRowId={(row) => row.id}
                components={{ Toolbar: GridToolbar }}
                density="compact"
                loading={loading}
                componentsProps={{
                  toolbar: {
                    csvOptions: { disableToolbarButton: false },
                    printOptions: { disableToolbarButton: false },
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 250 },
                  },
                }}
                rows={filteredProspects}
                columns={columns}
                pageSize={15}
                rowsPerPageOptions={[15, 25, 50]}
                sx={{
                  height: 667,
                  width: "100%",
                  borderRadius: 1,
                  backgroundColor: "background.paper",
                  boxShadow: 2,
                  "& .MuiDataGrid-cell:hover": {
                    color: "primary.main",
                  },
                  marginTop: 0,
                }}
              />
            )}
          </Grid>
        </Grid>
      </Card>

      <OnboardingCustomerWizard
        open={wizardOpen}
        onClose={handleCloseWizard}
        onComplete={handleWizardComplete}
      />

      {/* View Prospect Dialog */}
      <ViewProspectManagementDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedProspect(null);
        }}
        prospectId={selectedProspect?.id}
      />

      <ConvertProspectEntitiesDialog
        open={convertDialogOpen}
        onClose={() => {
          setConvertDialogOpen(false);
          setConvertProspect(null);
        }}
        onConverted={fetchProspects}
        prospectId={convertProspect?.id}
        prospectName={convertProspect?.companyName}
      />

      {/* Delete Prospect Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!deleting) {
            setDeleteDialogOpen(false);
            setNotesCount(0);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Prospect</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete the prospect <strong>{selectedProspect?.companyName}</strong>?
          </Typography>
          
          {notesCount > 0 && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 2, fontWeight: 500 }}>
              ⚠️ Warning: This will also delete {notesCount} note{notesCount !== 1 ? "s" : ""} associated with this prospect.
            </Typography>
          )}
          
          {entities.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This prospect has {entities.length} associated entit{entities.length === 1 ? "y" : "ies"}. 
                Select which entities should also be deleted:
              </Typography>
              <Box sx={{ maxHeight: "300px", overflowY: "auto" }}>
                {entities.map((entity) => {
                  const entityId = entity.entityId || entity.id;
                  return (
                    <FormControlLabel
                      key={entityId}
                      control={
                        <Checkbox
                          checked={selectedEntityIds.includes(entityId)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedEntityIds([...selectedEntityIds, entityId]);
                            } else {
                              setSelectedEntityIds(selectedEntityIds.filter((id) => id !== entityId));
                            }
                          }}
                        />
                      }
                      label={`${entity.entityNumber || "N/A"} - ${entity.name || "Unnamed Entity"}`}
                    />
                  );
                })}
              </Box>
              <Box sx={{ mt: 2 }}>
                <Button
                  size="small"
                  onClick={() => {
                    const allEntityIds = entities.map((e) => e.entityId || e.id);
                    if (selectedEntityIds.length === allEntityIds.length) {
                      setSelectedEntityIds([]);
                    } else {
                      setSelectedEntityIds(allEntityIds);
                    }
                  }}
                >
                  {selectedEntityIds.length === entities.length ? "Deselect All" : "Select All"}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : null}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default ProspectManagement;

